<template>
  <base-modal-component>
    <template v-slot:header>
      <div class="c-history-modal__heading">
        <h4 class="md-title u-typography-color">
          {{ historyLog.title }}
        </h4>
        <div class="c-history-modal__meta">
          <span :class="['c-history-badge', `c-history-badge--${action.tone}`]">
            <app-icon
              :name="action.icon"
              :size="14"
            />
            {{ action.label }}
          </span>
          <span
            v-if="userEmail"
            class="c-history-modal__meta-item"
          >
            <app-icon
              name="person"
              :size="16"
            />
            {{ userEmail }}
          </span>
          <span
            v-if="historyLog.createdAt"
            class="c-history-modal__meta-item"
          >
            <app-icon
              name="schedule"
              :size="16"
            />
            {{ dateTime }}
          </span>
        </div>
      </div>
    </template>

    <template v-slot:body>
      <div
        v-if="actionMessage || historyLog.message"
        class="ui-alert ui-alert--info c-history-modal__summary"
      >
        <app-icon name="info" />
        <div>
          <p v-if="actionMessage">
            {{ actionMessage }}
          </p>
          <p v-if="historyLog.message">
            {{ historyLog.message }}
          </p>
        </div>
      </div>

      <!-- field-by-field changes -->
      <section
        v-if="changes.length"
        class="c-history-modal__section"
      >
        <h5 class="c-history-modal__section-title">
          Changes
          <span>{{ changes.length }} {{ changes.length === 1 ? 'field' : 'fields' }}</span>
        </h5>

        <div
          v-for="(change, index) in changes"
          :key="index"
          class="c-history-diff"
        >
          <div class="c-history-diff__field">
            {{ change.key }}
          </div>
          <div class="c-history-diff__values">
            <div class="c-history-diff__value c-history-diff__value--old">
              <span class="c-history-diff__label">Before</span>
              <pre v-if="hasValue(change.old)">{{ format(change.old) }}</pre>
              <em v-else>empty</em>
            </div>
            <app-icon
              class="c-history-diff__arrow"
              name="arrow_forward"
              :size="18"
            />
            <div class="c-history-diff__value c-history-diff__value--new">
              <span class="c-history-diff__label">After</span>
              <pre v-if="hasValue(change.new)">{{ format(change.new) }}</pre>
              <em v-else>empty</em>
            </div>
          </div>
        </div>
      </section>

      <!-- full snapshots -->
      <section
        v-if="hasValue(description.old) || hasValue(description.new)"
        class="c-history-modal__section"
      >
        <h5 class="c-history-modal__section-title">
          {{ changes.length ? 'Full record' : 'Record' }}
        </h5>
        <div
          :class="['c-history-snapshots', { 'is-single': !(hasValue(description.old) && hasValue(description.new)) }]"
        >
          <div
            v-if="hasValue(description.old)"
            class="c-history-snapshot c-history-snapshot--old"
          >
            <span class="c-history-diff__label">Before</span>
            <pre>{{ format(description.old) }}</pre>
          </div>
          <div
            v-if="hasValue(description.new)"
            class="c-history-snapshot c-history-snapshot--new"
          >
            <span class="c-history-diff__label">After</span>
            <pre>{{ format(description.new) }}</pre>
          </div>
        </div>
      </section>

      <div
        v-if="!changes.length && !hasValue(description.old) && !hasValue(description.new)"
        class="c-table__empty"
      >
        <span class="c-table__empty-icon">
          <app-icon
            name="history"
            :size="24"
          />
        </span>
        <p class="c-table__empty-title">
          No details recorded
        </p>
        <p>This entry has no before/after data.</p>
      </div>
    </template>

    <template v-slot:footer>
      <generic-button
        variation="grey"
        @click="close()"
      >
        Close
      </generic-button>
    </template>
  </base-modal-component>
</template>

<script>
import GenericButton from "@/shared/buttons/GenericButton.vue";
import BaseModalComponent from "@/shared/modal/BaseModalComponent.vue";
import AppIcon from "@/shared/app-icon/AppIcon";
import { getHistoryLogAction, formatDateTime } from "@/shared/history-log/historyLogActions";

export default {
  components: {
    BaseModalComponent,
    GenericButton,
    AppIcon,
  },
  props: {
    historyLog: {
      type: Object,
      required: true,
    },
  },

  computed: {
    description() {
      return this.historyLog.description || {};
    },

    changes() {
      const changed = this.description.changed;

      // the old modal rendered any truthy `changed` with v-for, so objects are listed too
      if (Array.isArray(changed)) {
        return changed;
      }

      return changed && typeof changed === 'object' ? Object.values(changed) : [];
    },

    action() {
      return getHistoryLogAction(this.historyLog);
    },

    actionMessage() {
      return this.description.message;
    },

    userEmail() {
      return this.historyLog.user && this.historyLog.user.email;
    },

    dateTime() {
      return formatDateTime(this.historyLog.createdAt);
    },
  },

  methods: {
    hasValue(value) {
      return value !== undefined && value !== null && value !== '';
    },

    // objects/arrays are pretty-printed; JSON strings are parsed first so they read the same way
    format(value) {
      if (typeof value === 'string') {
        try {
          const parsed = JSON.parse(value);

          if (parsed && typeof parsed === 'object') {
            return JSON.stringify(parsed, null, 2);
          }
        } catch (e) {
          return value;
        }

        return value;
      }

      return typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value);
    },

    close() {
      this.$emit('close');
    },
  }
};
</script>

<style lang="scss" scoped>
// ---------- header ----------
.c-history-modal__heading {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

.c-history-modal__meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.c-history-modal__meta-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: var(--text-secondary);

  .c-app-icon {
    color: var(--text-muted);
  }
}

// ---------- body ----------
.c-history-modal__summary {
  margin-bottom: 20px;

  p {
    margin: 0;
  }
}

.c-history-modal__section {
  display: flex;
  flex-direction: column;
  gap: 12px;

  & + & {
    margin-top: 24px;
  }
}

.c-history-modal__section-title {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--text-secondary);

  span {
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0;
    text-transform: none;
    color: var(--text-muted);
  }
}

pre {
  margin: 0;
  max-height: 260px;
  overflow: auto;
  font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
  font-size: 12.5px;
  line-height: 1.55;
  white-space: pre-wrap;
  word-break: break-word;
  color: var(--text-primary);
}

// ---------- field diff ----------
.c-history-diff {
  border: 1px solid var(--border-default);
  border-radius: var(--radius-lg);
  overflow: hidden;
}

.c-history-diff__field {
  padding: 8px 16px;
  border-bottom: 1px solid var(--border-default);
  background-color: var(--bg-subtle);
  font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
}

.c-history-diff__values {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: stretch;
}

.c-history-diff__value {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  padding: 12px 16px;

  em {
    font-size: 13px;
    color: var(--text-muted);
  }

  &--old {
    background-color: var(--danger-bg);
  }

  &--new {
    background-color: var(--success-bg);
  }
}

.c-history-diff__arrow {
  align-self: center;
  margin: 0 4px;
  color: var(--text-muted);
}

.c-history-diff__label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-muted);
}

// ---------- snapshots ----------
.c-history-snapshots {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;

  &.is-single {
    grid-template-columns: 1fr;
  }

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
}

.c-history-snapshot {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  padding: 12px 16px;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-lg);
  background-color: var(--bg-subtle);

  pre {
    max-height: 50vh;
  }

  &--old {
    border-left: 3px solid var(--danger-fg);
  }

  &--new {
    border-left: 3px solid var(--success-fg);
  }
}
</style>
